import { ImmutableObject } from "@hookstate/core"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { Skeleton } from "@mui/material"

import { useRouter } from "next/router"

import { FC, useEffect, useMemo, useState } from "react"

import { useAccount } from "../../../../../states/account"
import { useBooking } from "../../../../../states/bookingDetail"
import { useBookings } from "../../../../../states/bookings"
import { Button } from "src/components/common/button"
import { ShopCartProductCard } from "src/components/services/shop/cart/ShopCartProductCard"
import { create } from "src/helpers/bem"
import { deepClone } from "src/helpers/deepClone"
import { getPage } from "src/helpers/pages"
import {
  fetchBillingOptions,
  postPurchaseorder,
  postPurchaseorderRefresh,
} from "src/queriesXRM/purchaseorder"
import { useLocale } from "src/states/locale"
import {
  CartProduct,
  postRefreshCart,
  resetCart,
  setCart,
  useCart,
} from "src/states/shop/carts"
import { ProductVariant } from "src/states/shop/productVariants"
import { TranslationMessages } from "src/translations"

import { PagesProps } from "src/types/SharedPageProps"

import { xRMApiPurchaseorder, xRMApiTermsOfService } from "src/types/xRM"

import styles from "./ShopCartOverview.module.scss"
import {
  BillingOptions,
  defaultBillingOptions,
  ShopCartOverviewBillingOptionsCard,
} from "./ShopCartOverviewBillingOptionsCard"
import { ShopCartOverviewCCInformationCard } from "./ShopCartOverviewCCInformationCard"
import { ShopCartOverviewSummary } from "./ShopCartOverviewSummary"
import { ShopCartOverviewTermsOfServicesCard } from "./ShopCartOverviewTermsOfServicesCard"

const bem = create(styles, "ShopCartOverview")

export type ShopCartOverviewProps = {
  messages: TranslationMessages
  pages: PagesProps
}

export type CCInformation = {
  name: string
  number: string
  expirationDate: string
  cvv: string
}

export const ShopCartOverview: FC<ShopCartOverviewProps> = ({
  messages,
  pages,
}) => {
  const locale = useLocale()
  const router = useRouter()
  const { booking } = useBooking()
  const { bookings } = useBookings()
  const { contact: { id: accountId } = {} } = useAccount()
  const cart = useCart({ id: router.query.cartId as string })
  const m = messages.pages.shop.cart.overview
  const [termsOfServices, setTermsOfServices] = useState<
    xRMApiTermsOfService[]
  >([])
  const [billingOptions, setBillingOptions] = useState<BillingOptions>(
    defaultBillingOptions,
  )
  const [loading, setLoading] = useState(false)
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false)
  const [selectedIds, setSelectedIds] = useState<(string | undefined)[]>(
    cart.products.flatMap((product) => product.id),
  )
  const [ccInformation, setCCInformation] = useState<CCInformation>({
    name: "",
    number: "",
    expirationDate: "",
    cvv: "",
  })
  const currentBooking = bookings.find((b) => b.id === booking?.id)
  const needsCCInformation =
    cart.products.filter((product) => product.productVariant?.ccRequested)
      .length > 0

  useEffect(() => {
    setLoading(true)
    const refreshCartAsync = async () =>
      setCart(await postRefreshCart(cart), true)
    refreshCartAsync().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const webshopOverviewPage = useMemo(
    () => getPage("Webshop Overview Template", locale, pages),
    [pages, locale],
  )

  const normalizeProducts = (
    products: CartProduct[],
    existingUniqueProducts?: CartProduct[],
  ) => {
    const normalizedProducts: CartProduct[] = existingUniqueProducts ?? []

    products.forEach((product) => {
      const filteredMandatoryProducts: ProductVariant[] = []

      product.productVariant?.mandatoryProducts?.forEach((mandatoryProduct) => {
        if (!mandatoryProduct.isUniqueInOrder) {
          filteredMandatoryProducts.push(mandatoryProduct)
          return
        }

        if (
          normalizedProducts.some((unique) =>
            unique.productVariant?.mandatoryProducts?.some(
              (uniqueProduct) => uniqueProduct.id == mandatoryProduct.id,
            ),
          )
        ) {
          return
        } else {
          filteredMandatoryProducts.push(mandatoryProduct)
        }
      })
      normalizedProducts.push({
        ...product,
        productVariant: {
          ...product.productVariant,
          mandatoryProducts: filteredMandatoryProducts,
        },
      })
    })

    return normalizedProducts
  }

  const normalizedProducts = useMemo(() => {
    const cartProducts = deepClone<CartProduct[]>(cart.products)
    const selectedProducts = cartProducts.filter((selected) =>
      selectedIds.some((id) => id === selected.id),
    )
    const unselectedProducts = cartProducts.filter(
      (selected) => !selectedIds.some((id) => id === selected.id),
    )

    const normalizedSelectedProducts = normalizeProducts(selectedProducts)
    const normalizedUnselectedProducts = normalizeProducts(
      unselectedProducts,
      normalizedSelectedProducts,
    )

    const uniqueProducts: CartProduct[] = cartProducts.map(
      (product) =>
        normalizedSelectedProducts.find(
          (selected) => selected.id === product.id,
        ) ??
        normalizedUnselectedProducts.find(
          (unselected) => unselected.id === product.id,
        ) ??
        product,
    )

    return uniqueProducts as ImmutableObject<CartProduct[]>
  }, [cart.products, selectedIds])

  const checkoutProducts = useMemo(() => {
    return normalizedProducts.filter((product) =>
      selectedIds.some((id) => id == product?.id),
    )
  }, [normalizedProducts, selectedIds])

  const checkout = async () => {
    const purchaseorder: xRMApiPurchaseorder = {
      parentSalesorderId: cart.id,
      id: cart.orderId,
      exhibition: {
        id: process.env.NEXT_PUBLIC_WEBSHOP_XRM_EVENT_ID,
      },
      products: deepClone<CartProduct[]>(checkoutProducts),
      agreements: termsOfServices.filter(
        (t) => t.termsOfService?.urlType === "GeneralTermsAndConditions",
      ),
      isPurchaseorderOnOwnAccount: billingOptions.orderOnOwnAccount,
      email: billingOptions.email,
      ccName: ccInformation.name,
      ccNumber: ccInformation.number,
      ccValidTo: ccInformation.expirationDate,
      cvvNumber: ccInformation.cvv,
    }
    const { mustRefresh } = await postPurchaseorder(purchaseorder)
    if (mustRefresh) {
      const refreshed = await postPurchaseorderRefresh(purchaseorder)
      setCart(refreshed.products ?? [], true)
    } else {
      const remainingProducts = normalizedProducts.filter(
        (cartProduct) =>
          !checkoutProducts.some(
            (checkoutProduct) => checkoutProduct.id == cartProduct.id,
          ),
      )
      resetCart(cart, deepClone<CartProduct[]>(remainingProducts))
    }
  }

  useEffect(() => {
    setIsLoadingPermissions(true)
    const fetchBillingOptionsAsync = async () => {
      const fetchedCheckoutPermission = await fetchBillingOptions(cart.id)
      setBillingOptions({
        orderOnOwnAccount:
          fetchedCheckoutPermission.checkoutPermission === "BuyOnOwnAccount",
        permission: fetchedCheckoutPermission,
        email: "",
        emailIsValid: false,
      })
    }
    fetchBillingOptionsAsync().finally(() => setIsLoadingPermissions(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={bem()}>
      <Button
        startIcon={<ArrowBackIcon />}
        title={m.backToShop}
        variant="text"
        onClick={async () =>
          await router.push({
            pathname: webshopOverviewPage?.uri as string,
            query: {
              cartId: router.query.cartId,
            },
          })
        }
      />
      <div className={bem("container")}>
        <div className={bem("items")}>
          {loading ? (
            <div className={bem("skeleton")}>
              <Skeleton
                className={bem("skeleton__first")}
                height={70}
                variant="rectangular"
                width={70}
              />
              <Skeleton
                className={bem("skeleton__second")}
                height={100}
                variant="rectangular"
                width={100}
              />
              <div className={bem("skeleton-items")}>
                <Skeleton height={40} width={200} />
                <Skeleton
                  className={bem("skeleton-item")}
                  height={40}
                  width={100}
                />
                <Skeleton
                  className={bem("skeleton-item")}
                  height={40}
                  width={100}
                />
              </div>
            </div>
          ) : normalizedProducts && normalizedProducts?.length > 0 ? (
            normalizedProducts.map((product, index) => (
              <ShopCartProductCard
                key={product.id + index.toString()}
                item={product}
                selected={selectedIds.some((id) => id === product.id)}
                onSelect={(_ev, checked) => {
                  if (checked) setSelectedIds([...selectedIds, product.id])
                  else
                    setSelectedIds([
                      ...selectedIds.filter((id) => id !== product.id),
                    ])
                }}
              />
            ))
          ) : (
            <div className={bem("noData")}>{m.noData}</div>
          )}
        </div>
        <div className={bem("summary")}>
          <ShopCartOverviewTermsOfServicesCard
            isCategoryRelated={true}
            setTermsOfServices={setTermsOfServices}
            termsOfServices={termsOfServices}
          />
          <ShopCartOverviewTermsOfServicesCard
            isCategoryRelated={false}
            setTermsOfServices={setTermsOfServices}
            termsOfServices={termsOfServices}
          />

          {(currentBooking?.senderContactId !== accountId ||
            currentBooking?.contactId !== accountId) && (
            <ShopCartOverviewBillingOptionsCard
              billingOptions={billingOptions}
              isLoading={isLoadingPermissions}
              setBillingOptions={setBillingOptions}
            />
          )}
          {needsCCInformation && (
            <ShopCartOverviewCCInformationCard
              ccInformation={ccInformation}
              needsCCInformation={needsCCInformation}
              updateCCInformation={setCCInformation}
            />
          )}
          <ShopCartOverviewSummary
            billingOptions={billingOptions}
            ccInformation={ccInformation}
            checkoutProducts={checkoutProducts}
            needsCCInformation={needsCCInformation}
            pages={pages}
            termsOfServices={termsOfServices}
            onCheckout={checkout}
          />
        </div>
      </div>
    </div>
  )
}
